import { useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import CsvImporter from "@/components/CsvImporter";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { listApprovedBookers, createApprovedBooker } from "@/lib/api";

const COLUMNS = [
  { key: "email",        label: "Email",        required: true, description: "Carrier contact email (becomes the booker's contact email).", example: "dispatch@acme.com.au" },
  { key: "company_name", label: "Company",      description: "Carrier company. Booker is named \"Company (Contact)\".", example: "Acme Transport" },
  { key: "contact_name", label: "Contact",      description: "Contact person's name.", example: "Jane Smith" },
  { key: "phone",        label: "Phone",        description: "Contact phone.", example: "0400 000 000" },
  { key: "abn",          label: "ABN",          description: "Optional 11-digit ABN.", example: "" },
  { key: "is_active",    label: "Active",       description: "true / false. Blank = true. Dormant carriers import as inactive.", example: "true" },
  { key: "auto_approve", label: "Auto-approve", description: "true / false. Blank = false.", example: "false" },
  { key: "notes",        label: "Notes",        description: "Internal notes (legacy info, contact, last login).", example: "" },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const bool = (v, dflt) => (v === "" || v == null ? dflt : /^(true|yes|y|1)$/i.test(v.trim()));

function bookerName(company, contact, email) {
  const co = company.trim();
  let c = contact.trim();
  if (co && c.toLowerCase().endsWith(co.toLowerCase()) && c.length > co.length) {
    c = c.slice(0, c.length - co.length).trim();
  }
  if (co && c && c.toLowerCase() !== co.toLowerCase()) return `${co} (${c})`;
  return co || c || email.split("@")[0];
}

export default function AdminImportApprovedBookers() {
  const { assignments } = useAuth();
  const { viewAsClientId, isSuperAdmin } = useViewAsClient();
  const targetClientId = isSuperAdmin ? (viewAsClientId || null) : (assignments?.[0]?.client_id || null);

  const loadContext = useCallback(async () => {
    if (!targetClientId) {
      throw new Error(isSuperAdmin
        ? "Super admin: please pick a client in the top bar first."
        : "Your account has no tenant assignment.");
    }
    const { data, error } = await listApprovedBookers({ clientId: targetClientId, includeInactive: true });
    if (error) throw error;
    return {
      clientId: targetClientId,
      byName: new Map((data || []).map((b) => [b.name.toLowerCase(), b])),
      byEmail: new Map((data || []).map((b) => [(b.contact_email || "").toLowerCase(), b])),
    };
  }, [targetClientId, isSuperAdmin]);

  const validateAll = (raws, ctx) => {
    const seen = new Map();
    return raws.map((raw) => {
      const errors = [];
      const warns = [];
      const email = (raw.email || "").trim().toLowerCase();
      const company = raw.company_name || "";
      const contact = raw.contact_name || "";

      if (!email) errors.push("email required");
      else if (!EMAIL_RE.test(email)) errors.push(`invalid email "${email}"`);

      let name = bookerName(company, contact, email || "unknown");
      const n = (seen.get(name.toLowerCase()) || 0) + 1;
      seen.set(name.toLowerCase(), n);
      if (n > 1) {
        name = `${name} #${n}`;
        warns.push("duplicate name in file — numbered");
      }

      const existing = ctx.byName.get(name.toLowerCase());
      if (existing) errors.push(`already exists in this tenant ("${existing.name}")`);
      const sameEmail = ctx.byEmail.get(email);
      if (email && sameEmail && !existing) warns.push(`email already used by "${sameEmail.name}"`);

      if (!company.trim()) warns.push("no company — named after contact");
      if (!(raw.phone || "").trim()) warns.push("no phone");
      const abn = (raw.abn || "").replace(/\s/g, "");
      if (abn && !/^\d{11}$/.test(abn)) warns.push("ABN is not 11 digits");
      const isActive = bool(raw.is_active, true);
      if (!isActive) warns.push("imports as INACTIVE (dormant)");

      return {
        level: errors.length ? "error" : warns.length ? "warn" : "valid",
        errors: [...errors, ...warns],
        normalised: {
          client_id: ctx.clientId,
          name,
          contact_email: email,
          contact_phone: (raw.phone || "").trim(),
          abn,
          notes: (raw.notes || "").trim(),
          is_active: isActive,
          auto_approve_bookings: bool(raw.auto_approve, false),
        },
      };
    });
  };

  const commitRow = async (row) => {
    const { error } = await createApprovedBooker(row);
    return error ? { ok: false, error: error.message } : { ok: true };
  };

  const reconcile = async (rows, ctx) => {
    const { data, error } = await listApprovedBookers({ clientId: ctx.clientId, includeInactive: true });
    if (error) throw error;
    const names = new Set((data || []).map((b) => b.name.toLowerCase()));
    return new Set(rows.map((r, i) => (names.has(r.name.toLowerCase()) ? -1 : i)).filter((i) => i >= 0));
  };

  return (
    <div className="space-y-3" data-testid="import-approved-bookers-page">
      <Link to="/admin/approved-bookers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-bookers-link">
        <ArrowLeft className="h-4 w-4" /> Approved Bookers
      </Link>
      <CsvImporter
        title="Import carriers (Approved Bookers)"
        testid="import-bookers"
        columns={COLUMNS}
        loadContext={loadContext}
        validateAll={validateAll}
        commitRow={commitRow}
        reconcile={reconcile}
        previewLabel={(r) => (r.normalised ? `${r.normalised.name}${r.normalised.is_active ? "" : " · inactive"}` : "")}
      />
    </div>
  );
}
